"use client";

import {
  Sparkles,
  Upload,
  Gauge,
  Tag,
  MapPin,
  Search,
  CheckCircle2,
  Layers,
  SlidersHorizontal,
  BadgeCheck,
  RotateCcw,
  RefreshCw,
  Network,
  Shield,
  Globe2,
} from "@/components/icons";
import { CHROME_EXTENSION_URL, FIREFOX_EXTENSION_URL } from "@/lib/constants";
import { chromeFaqs, firefoxFaqs } from "./faqs";
import type {
  Browser,
  ExtensionConfig,
  ExtensionFaq,
  ExtensionFeature,
  ExtensionPermission,
  ExtensionShot,
  ExtensionStep,
} from "./types";

/**
 * Feature set shared by both browsers. The extension is the same product on
 * Chrome and Firefox, so parity is intentional. Only browser-specific copy
 * (labels, store, hero, featured badge, FAQs) differs, via buildConfig below.
 */
const FEATURES: ExtensionFeature[] = [
  {
    icon: Sparkles,
    title: "Add proxies in any format",
    description:
      "Paste ip:port, user:pass@ip:port, or full http:// and socks5:// URLs. Host, port, and credentials are separated for you.",
  },
  {
    icon: Upload,
    title: "Bulk paste & CSV import",
    description: "Import your entire proxy pool at once by pasting a list or uploading a CSV file.",
  },
  {
    icon: Gauge,
    title: "Built for huge lists",
    description:
      "Thousands of proxies stay fast and organized, so large pools never slow you down.",
  },
  {
    icon: Tag,
    title: "Nickname every proxy",
    description:
      "Label proxies by region, account, or project to keep sessions and workflows tidy.",
  },
  {
    icon: MapPin,
    title: "Country detection with flags",
    description: "See where each proxy exits at a glance, with the country detected automatically.",
  },
  {
    icon: Search,
    title: "Instant search & pagination",
    description:
      "Find the right proxy fast. The popup even remembers your search and page while you work.",
  },
  {
    icon: CheckCircle2,
    title: "Built-in proxy tester",
    description:
      "Check connectivity, authentication, and the real exit IP before you route traffic through a proxy.",
  },
  {
    icon: Layers,
    title: "Batch testing",
    description: "Test one proxy or your whole list in a single run, with clear pass/fail results.",
  },
  {
    icon: SlidersHorizontal,
    title: "One-click connect & switch",
    description:
      "Turn a proxy on or off and switch instantly. Credentials apply automatically, with no login popups.",
  },
  {
    icon: BadgeCheck,
    title: "Toolbar status badge",
    description: "The toolbar badge always shows which proxy you're connected to.",
  },
  {
    icon: RotateCcw,
    title: "Auto-reload on change",
    description:
      "Optionally reload the current tab or every tab when your proxy changes, so you never browse on a stale IP.",
  },
  {
    icon: RefreshCw,
    title: "On-demand IP rotation",
    description:
      "Get a fresh IP in one click, with session rotation for residential gateways and rotation links for mobile and dedicated proxies.",
  },
  {
    icon: Network,
    title: "Per-site smart routing",
    description:
      "Keep one main proxy and add per-site exceptions, with a global bypass list and a master switch for every rule.",
  },
  {
    icon: Shield,
    title: "WebRTC leak protection",
    description:
      "Stops your real IP from leaking through WebRTC while connected. The indicator turns into a shield so you can see it working.",
  },
  {
    icon: Globe2,
    title: "HTTP, HTTPS & SOCKS",
    description:
      "Full support for HTTP, HTTPS, and SOCKS proxies from any provider. Pair it with Proxidize for the best results.",
  },
];

const buildIntro = (browserLabel: string): string[] => [
  `Proxidize Proxy Extension is a complete proxy manager built right into ${browserLabel}. Add proxies from any provider, test them before you trust them, connect in one click, rotate IPs on demand, and decide exactly which sites go through which proxy.`,
  "No account needed, and it works with every proxy provider. Think of it as a modernized version of the traditional “Proxy Settings” interface: simpler, faster, and far more efficient.",
  `Configure all your proxies once, then connect, switch, and rotate straight from the toolbar, with no digging through ${browserLabel}'s advanced settings or your operating system's proxy dialog.`,
];

const buildSteps = (browserLabel: string, storeName: string): ExtensionStep[] => [
  {
    title: "Install the extension",
    description: `Add Proxidize to ${browserLabel} in one click from the ${storeName}.`,
  },
  {
    title: "Add your proxies",
    description:
      "Paste proxies in any format, bulk-paste a list, or upload a CSV. Type, exit IP, and country are detected automatically.",
  },
  {
    title: "Connect, switch & rotate",
    description:
      "Test that a proxy works, connect in one click, then switch between proxies or rotate your IP whenever you need to.",
  },
];

const buildScreenshots = (browserLabel: string): ExtensionShot[] => [
  {
    label: "Extension popup",
    caption: `Your proxy manager, right in the ${browserLabel} toolbar.`,
    src: "/snapshot.png",
  },
  {
    label: "Importing proxies",
    caption: "Bulk-paste or CSV import with automatic detection.",
    src: "/b1b0b2de6deb44fb64210f50752cd9a6.png",
  },
  {
    label: "Quick settings",
    caption:
      "Toggle smart routing, WebRTC leak protection, and auto-reload on proxy change, right from the popup.",
    src: "/quick-settings.png",
  },
];

const buildPermissions = (
  browserLabel: string
): { required: ExtensionPermission[]; optional: ExtensionPermission[] } => ({
  required: [
    {
      label: "Control browser proxy settings",
      why: `So the extension can route ${browserLabel} through the proxy you select.`,
    },
    {
      label: "Clear recent browsing history, cookies, and related data",
      why: "To reset session data when you switch proxies, preventing leaks between sessions.",
    },
  ],
  optional: [
    {
      label: "Access your data for all websites",
      why: "Used only to detect the active proxy's IP and country. You can decline this and still use the core features.",
    },
  ],
});

interface BrowserMeta {
  browser: Browser;
  browserLabel: string;
  storeName: string;
  storeUrl: string;
  storeUrlLabel: string;
  storeCta: string;
  eyebrow: string;
  heading: string;
  tagline: string;
  heroImage: string;
  heroVideo?: { src: string; width: number; height: number };
  featured?: string;
  faqs: ExtensionFaq[];
  faqDescription: string;
}

const buildConfig = (meta: BrowserMeta): ExtensionConfig => ({
  browser: meta.browser,
  browserLabel: meta.browserLabel,
  storeName: meta.storeName,
  storeUrl: meta.storeUrl,
  storeUrlLabel: meta.storeUrlLabel,
  storeCta: meta.storeCta,
  featured: meta.featured,
  eyebrow: meta.eyebrow,
  heading: meta.heading,
  tagline: meta.tagline,
  heroImage: meta.heroImage,
  heroVideo: meta.heroVideo,
  heroPerks: [
    "Free, no account needed",
    "Works with any proxy provider",
    "HTTP(S) & SOCKS support",
  ],
  intro: buildIntro(meta.browserLabel),
  features: FEATURES,
  steps: buildSteps(meta.browserLabel, meta.storeName),
  screenshots: buildScreenshots(meta.browserLabel),
  permissions: buildPermissions(meta.browserLabel),
  faqs: meta.faqs,
  faqDescription: meta.faqDescription,
});

const chrome = buildConfig({
  browser: "chrome",
  browserLabel: "Chrome",
  storeName: "Chrome Web Store",
  storeUrl: CHROME_EXTENSION_URL,
  storeUrlLabel:
    "https://chromewebstore.google.com/detail/proxidize-proxy-extension/gccdbcipcjgfnjlegofdhflkifgkdmpb",
  storeCta: "Add to Chrome",
  featured: "Top rated · Featured by Chrome",
  eyebrow: "Chrome Extension",
  heading: "The best proxy extension for Chrome",
  tagline:
    "A complete proxy manager built into Chrome. Add proxies from any provider, test them, connect in one click, and rotate your IP on demand.",
  heroImage: "/chrome.gif",
  heroVideo: { src: "/chrom.mp4", width: 1440, height: 796 },
  faqs: chromeFaqs,
  faqDescription:
    "Everything you need to know about the Proxidize proxy extension for Chrome: installation, proxy support, testing, IP rotation, per-site routing, and how it handles your data.",
});

const firefox = buildConfig({
  browser: "firefox",
  browserLabel: "Firefox",
  storeName: "Firefox Add-ons",
  storeUrl: FIREFOX_EXTENSION_URL,
  storeUrlLabel: "https://addons.mozilla.org/en-GB/firefox/addon/proxidize-proxy-extension/",
  storeCta: "Get for Firefox",
  eyebrow: "Firefox Extension",
  heading: "The easiest way to manage proxies in Firefox",
  tagline:
    "A complete proxy manager built into Firefox. Add proxies from any provider, test them, connect in one click, and rotate your IP on demand.",
  heroImage: "/firefox.gif",
  heroVideo: { src: "/firefox.mp4", width: 1728, height: 972 },
  faqs: firefoxFaqs,
  faqDescription:
    "Everything you need to know about the Proxidize proxy extension for Firefox: installation, proxy support, testing, IP rotation, per-site routing, and how it handles your data.",
});

export const EXTENSION_CONFIGS: Record<Browser, ExtensionConfig> = { chrome, firefox };

interface RelatedLink {
  label: string;
  href: string;
}

export const EXTENSION_RELATED_CONTENT: Record<
  Browser,
  { blogLinks: RelatedLink[]; useCaseLinks: RelatedLink[] }
> = {
  chrome: {
    blogLinks: [
      { label: "Best Chrome Proxy Extensions in 2026", href: "/blog/best-proxy-extensions" },
      { label: "Proxy SwitchyOmega / ZeroOmega Guide", href: "/blog/proxy-switchyomega" },
    ],
    useCaseLinks: [
      { label: "Web Scraping", href: "/use-cases/web-scraping" },
      { label: "Geolocation Testing", href: "/use-cases/localized-testing/" },
    ],
  },
  firefox: {
    blogLinks: [
      { label: "Proxy SwitchyOmega / ZeroOmega Guide", href: "/blog/proxy-switchyomega" },
      { label: "Browser Fingerprinting", href: "/blog/browser-fingerprinting" },
    ],
    useCaseLinks: [
      { label: "Web Scraping", href: "/use-cases/web-scraping" },
      { label: "Geolocation Testing", href: "/use-cases/localized-testing/" },
    ],
  },
};
